import { ApolloQueryResult } from '@apollo/client';
import { print } from 'graphql';
import gql from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { client } from '../../graphql/Client';
import { BaseEntity } from '../../models/BaseEntity';
import MigrationHelper from '../MigrationHelper';
import { generatePullQuery, PullQueryContext } from '../queryBuilders/generatePullQuery';
import RxdbCollectionName from '../rxdbCollectionName';
import { defaultDocAllTime } from '../Shared/defaultDoc';
import { CheckpointType } from '../types';

export interface BorerStateType extends BaseEntity {
  readonly description: string;
  readonly definition: string | null;
  readonly isRunning: boolean;
  readonly isDefault: boolean;
  readonly isActive: boolean;
  readonly borerStateTypeCategoryId: string | null;
  readonly timeUsageTypeId: string | null;
  readonly isPlanned: boolean | null;
  readonly equipmentTypeId: string | null;
  readonly siteId: string;
}
export type BorerStateTypeCollection = RxCollection<BorerStateType> | null;
export type BorerStateTypeDocument = RxDocument<BorerStateType>;

export const borerStateTypeSchema: RxJsonSchema<BorerStateType> = {
  type: 'object',
  version: 9,
  description: 'describes a BorerStateType object',
  primaryKey: 'id',
  properties: {
    id: { type: 'string', maxLength: 36 },
    updatedAt: { type: 'number' },
    isDeleted: { type: 'boolean' },
    version: { type: 'number' },
    description: { type: 'string' },
    definition: { type: ['string', 'null'] },
    isRunning: { type: 'boolean' },
    isDefault: { type: ['boolean', 'null'] },
    isActive: { type: ['boolean', 'null'] },
    borerStateTypeCategoryId: {
      type: ['string', 'null'],
      ref: RxdbCollectionName.BORER_STATE_TYPE_CATEGORY,
    },
    timeUsageTypeId: {
      type: ['string', 'null'],
    },
    isPlanned: { type: ['boolean', 'null'] },
    equipmentTypeId: { type: 'string' },
    siteId: { type: 'string' },
  },
};

export const borerStateTypeMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: BorerStateTypeDocument) => ({
    ...oldDoc,
    rtDuetReasonCodeId: '',
  }),
  2: (oldDoc: BorerStateTypeDocument) => ({
    ...oldDoc,
    isDefault: oldDoc.isDefault || null,
  }),
  3: (oldDoc: BorerStateTypeDocument) => {
    const newDoc = {
      ...oldDoc,
    };
    delete newDoc.rtDuetReasonCodeId;
    return newDoc;
  },
  4: (oldDoc: BorerStateTypeDocument) => oldDoc,
  5: (oldDoc: BorerStateTypeDocument) => oldDoc,
  6: (oldDoc: BorerStateTypeDocument) => ({ ...oldDoc, isDefault: oldDoc.isDefaultNonRunning }),
  7: (oldDoc: BorerStateTypeDocument) => {
    return MigrationHelper.instance.timeUsageTypeIdForStateTypeId(oldDoc.id).then(response => {
      const { timeUsageTypeId, isPlanned } = response;
      return { ...oldDoc, timeUsageTypeId, isPlanned };
    });
  },
  8: (oldDoc: BorerStateTypeDocument) => {
    return MigrationHelper.instance.equipmentTypeIdForStateTypeId(oldDoc.id).then(response => {
      const { equipmentTypeId, siteId } = response;
      if (equipmentTypeId && siteId) {
        return { ...oldDoc, equipmentTypeId, siteId };
      }
      // If the equipmentTypeId or siteId is null, that means the oldDoc.id is not in new
      // listBorerStateTypes query that includes equipmentTypeId as input param.
      // If we don't have the equipmentTypeId or siteId, we don't need to migrate this document
      return null;
    });
  },
  9: (oldDoc: BorerStateTypeDocument) => ({ ...oldDoc }),
};

const borerStateTypeFeedQuery = print(gql`
  query borerStateTypeFeed($limit: Int!, $lastUpdateAt: Float!, $siteId: ID!) {
    borerStateTypeFeed(siteId: $siteId, lastUpdateAt: $lastUpdateAt, limit: $limit) {
      id
      updatedAt
      version
      isDeleted
      description
      definition
      isRunning
      isDefault
      borerStateTypeCategoryId
      isActive
      timeUsageTypeId
      isPlanned
      equipmentTypeId
      siteId
    }
  }
`);

const basePullQueryBuilder = generatePullQuery(
  borerStateTypeFeedQuery,
  PullQueryContext.Site,
  defaultDocAllTime,
);
let initialPullQuery = true;

export const borerStateTypePullQueryBuilder = (
  lastPulledCheckpoint: CheckpointType | undefined,
  limit: number,
) => {
  // HACK: Force initial re-pull of all borer state type feed data to ensure new 'definition' field is populated.
  // This addresses the issue where existing records in RxDB don't get updated when new fields are added,
  // because the backend records haven't actually been "updated" (their updatedAt timestamp remains the same).
  // By ignoring the lastPulledCheckpoint on the first query, we force RxDB to re-pull all existing records,
  // which will then include the new 'definition' field and trigger the migration to set default values.
  // This ensures users get the new field without requiring a full RxDB reset.
  // TODO: remove this once all clients have been updated and re-synced the borer state type feed data
  if (initialPullQuery) {
    initialPullQuery = false;
    // Instead of passing null, create a new checkpoint object.
    // This forces a full re-sync by setting updatedAt to 1,
    // while preserving the ID of the last document for context.
    const forcedSyncCheckpoint: CheckpointType = {
      id: lastPulledCheckpoint?.id || defaultDocAllTime.id,
      updatedAt: 1,
    };
    return basePullQueryBuilder(forcedSyncCheckpoint, limit);
  }
  return basePullQueryBuilder(lastPulledCheckpoint, limit);
};

const listBorerStateTypesQuery = gql`
  query listBorerStateTypes($siteId: ID!, $equipmentTypeId: ID!) {
    listBorerStateTypes(siteId: $siteId, equipmentTypeId: $equipmentTypeId, includeInactive: true) {
      id
      description
      isActive
      isRunning
      borerStateTypeCategory {
        id
        description
      }
      timeUsageType {
        description
        id
      }
      isPlanned
      siteId
      equipmentType {
        id
        description
      }
    }
  }
`;

export interface BstFromListBorerStateTypeQuery extends Omit<BorerStateType, 'equipmentTypeId'> {
  equipmentType: {
    id: string;
    description: string;
  };
}

interface ListBorerStateTypesResponse {
  listBorerStateTypes: BstFromListBorerStateTypeQuery[];
}

export const listBorerStateTypes = (
  siteId: string,
  equipmentTypeId: string,
): Promise<ApolloQueryResult<ListBorerStateTypesResponse>> =>
  client.query({
    query: listBorerStateTypesQuery,
    variables: { siteId, equipmentTypeId },
    fetchPolicy: 'cache-first',
  });
